import { mapMutations, mapState } from 'vuex';
import ApiService from '@/services/apiService';
import iotFetchMixin from './iotFetchMixin';
import AddressLatLongMixin from '@/components/core/addressLatLongMixin';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import unset from 'lodash/unset';
const ITEMS_PER_PAGE = 5;
const defaultTableOptions = {
  page: 1,
  itemsPerPage: ITEMS_PER_PAGE
};
const vDeviceLookupMultipleMatches = 'MULTIPLE_MATCHES';
export default {
  name: 'CoreDtwinDuplicateVirtualDevices',
  mixins: [iotFetchMixin, AddressLatLongMixin],
  props: {
    orgTwin: {
      type: Object,
      required: true
    },
    orgTwinStates: {
      type: Array,
      required: true
    },
    twinCopiesData: {
      type: Array,
      required: true
    },
    statePickerMode: {
      type: String,
      required: true
    },
    errornousParentId: {
      type: Number,
      required: true
    },
    bridgeConfigs: {
      type: Array,
      required: true
    },
    globalParentTwin: {
      type: Object,
      required: true
    },
    // eslint-disable-next-line vue/require-prop-types
    globalParentId: {
      // is either false or a number
      required: true
    },
    loadingStates: {
      type: Boolean,
      required: true
    },
    pickableStates: {
      type: Array,
      required: true
    },
    metaInfoToggles: {
      type: Object,
      required: true
    }
  },
  data: function () {
    return {
      vDeviceFetchMapping: {
        // map the searched vdevice findById request here
        vdevice: {},
        // map the states-fetch-requests for the vdevice above here, to match with orgState
        vdeviceStates: {},
        // map the get-request for the original `stateId` of the org vdevice here
        vdeviceOrgState: {}
      },
      tableOptions: {
        ...defaultTableOptions
      },
      showOptionsMenus: {},
      disableFetchOnCreatedMixin: true,
      itemsPerPage: ITEMS_PER_PAGE
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color']),
    titlePlaceholder() {
      return `${this.orgTwin.title} copy [X]`;
    },
    tableFooterProps() {
      return {
        itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
        itemsPerPageOptions: [3]
      };
    },
    noSourceType() {
      return `${this.$i18n_t('DigitalTwin.DigitalTwin.states.copy.sourceType')}: ${this.$i18n_t('DigitalTwin.DigitalTwin.states.dataSources.none')}`;
    }
  },
  methods: {
    ...mapMutations('app', ['snackbar']),
    get,
    cloneDeep,
    setCurrentTablePage(value) {
      this.tableOptions.page = value;
    },
    setIotHubEuiFromOriginalState(state, pickableState) {
      this.fetchIotHubDeviceEui(pickableState).then(finalEui => {
        if (finalEui) {
          this.vModelForState(state).eui = finalEui;
          this.$emit('cloneOrgTwinStates');
        }
      }).catch(error => {
        console.error(error);
      });
    },
    setVirtualDeviceExternalIdFromOriginalState(state, pickableState) {
      this.fetchVirtualDeviceExternalId(pickableState).then(externalId => {
        if (externalId) {
          this.vModelForState(state).vdExternalId = externalId;
          this.$emit('cloneOrgTwinStates');
        }
      }).catch(error => {
        console.error(error);
      });
    },
    pickableTargetStates(twin) {
      return this.pickableStates.map(orgState => {
        return twin.states.find(s => s.id === orgState.id);
      });
    },
    toggleStateBindingType(matchingState, orgState, bindingType = 'none', cloneOriginalStates = true) {
      if (matchingState.properties.binding.type === bindingType) {
        matchingState.properties.binding.type = orgState.properties.binding.type;
      } else {
        unset(matchingState, 'properties.binding.options.virtualDevice.vDeviceId');
        matchingState.properties.binding.type = bindingType;
        // The v-model needs to bind to something, so the display-text of "disabled" states
        // is also set to vdExternalId of a target-model in vModelForState
        // this is not modifiable and just for output
        let targetProp = 'vdExternalId';
        if (orgState.properties.binding.type.startsWith('iothub')) {
          targetProp = 'eui';
        }
        matchingState.properties[targetProp] = bindingType === 'none' ? this.noSourceType : this.$i18n_t('DigitalTwin.DigitalTwin.copy.noStateCopy');
      }
      if (cloneOriginalStates) {
        this.$emit('cloneOrgTwinStates');
      }
    },
    matchedState(item, orgState) {
      return item.states.find(s => s.id === orgState.id);
    },
    vModelForState(matchingState) {
      if (matchingState.properties.binding.type.startsWith('virtual-device')) {
        return matchingState.properties.binding.options.virtualDevice;
      }
      if (matchingState.properties.binding.type.startsWith('iothub')) {
        return matchingState.properties.binding.options;
      }
      return matchingState.properties;
    },
    isLoadingStateData(matchingState) {
      if (matchingState.properties.binding.type.startsWith('virtual-device')) {
        return !!matchingState.properties.binding.options.virtualDevice.vdExternalId && !matchingState.properties.binding.options.virtualDevice.vDeviceKey;
      }
      if (matchingState.properties.binding.type.startsWith('iothub')) {
        return !!matchingState.properties.binding.options.identifier;
      }
      return false;
    },
    prependIconForState(matchingState) {
      if (matchingState.properties.binding.type.startsWith('virtual-device')) {
        return 'fa-sensor-on';
      }
      if (matchingState.properties.binding.type.startsWith('iothub')) {
        return 'fa-server';
      }
      return matchingState.properties.binding.type === 'none' ? 'fa-save' : 'fa-ban';
    },
    rulesForState(matchingState) {
      if (!matchingState.properties.binding.type.startsWith('virtual-device') && !matchingState.properties.binding.type.startsWith('iothub')) {
        return [];
      }
      if (matchingState.properties.binding.type.startsWith('iothub')) {
        return this.euiRules;
      }
      // do not mark the state as invalid (in the UI!) until the state-get request was completed,
      // with a find or a 404
      return [v => !v && !matchingState.deviceTitle || !!v || this.$i18n_t('Common.fieldRequired'), v => !v || matchingState.deviceTitle === undefined || !!matchingState.deviceTitle && !!matchingState.properties.binding.options.virtualDevice.vDeviceKey || this.$i18n_t('Error.DTWIN_STATE_NOT_FOUND')];
    },
    hintForState(matchingState) {
      if (!matchingState.properties.binding.type.startsWith('virtual-device')) {
        return null;
      }
      if (matchingState.deviceTitle && matchingState.properties.binding.options.virtualDevice.vDeviceKey) {
        return `${matchingState.deviceTitle} - ${matchingState.properties.binding.options.virtualDevice.vDeviceKey}`;
      }
      return null;
    },
    twinParentFilter(twin) {
      return !get(twin, 'properties.disabled');
    },
    updateTwinsParents(parentTwin) {
      this.twinCopiesData.forEach(twin => {
        if (this.$refs[`twin-finder-${twin.id}`]) {
          this.$refs[`twin-finder-${twin.id}`].digitalTwin = null;
        }
        twin.parentId = parentTwin.id;
      });
    },
    handleCoordinateChange(twin) {
      this.handleParsingCoordinates(twin.coordinates.coordinates);
    },
    updateTags(tags, twin) {
      twin.data.tags = tags;
    },
    fetchOptionsFromVDState(vdState) {
      const fetchOptions = {
        id: vdState.properties.binding.options.virtualDevice.vdExternalId,
        stateId: vdState.properties.binding.options.virtualDevice.stateId
      };
      if (vdState.properties.binding.type === 'virtual-device-aggregate') {
        // for aggregate-states, inside the UI only verify the first wired state
        // the rest has to be done in the BE for simplicity...
        fetchOptions.stateId = get(vdState, 'properties.binding.options.virtualDevice.stateIds[0]');
      }
      return fetchOptions;
    },
    fetchVirtualDeviceExternalId(virtualDeviceState) {
      const deviceId = virtualDeviceState.properties.binding.options.virtualDevice.id;
      const foundMapping = Object.values(this.vDeviceFetchMapping).find(mapping => {
        return mapping.id === deviceId;
      });
      if (foundMapping) {
        const externalId = get(foundMapping, 'properties.bridge.deviceData.device_id');
        return Promise.resolve(externalId);
      }
      return ApiService.virtualDeviceGet(this, deviceId, 'bare').then(res => {
        const externalId = get(res, 'properties.bridge.deviceData.device_id');
        if (externalId) {
          this.vDeviceFetchMapping['vdevice'][externalId] = res;
        }
        return externalId;
      }).catch(() => {
        return null;
      });
    },
    fetchVirtualDeviceData(state, twin, cloneOriginalStates = true) {
      if (!get(state, 'properties.binding.options.virtualDevice.vdExternalId')) {
        return;
      }
      state.deviceTitle = null;
      const fetchOptions = this.fetchOptionsFromVDState(state);
      return this.fetchVirtualDeviceDataDetails(fetchOptions).then(details => {
        state.properties.binding.options.virtualDevice = {
          ...state.properties.binding.options.virtualDevice,
          vDeviceStateId: details.stateId,
          vDeviceId: details.id,
          vDeviceKey: details.key
        };

        // if there are multiple matches for the same device_id, set this states' source to `none`
        if (details.id === vDeviceLookupMultipleMatches) {
          this.toggleStateBindingType(state, state, 'none', cloneOriginalStates);
        }
        state.deviceTitle = details.title;
        if (twin && cloneOriginalStates) {
          twin.states = cloneDeep(twin.states);
        }
        if (cloneOriginalStates) {
          this.$emit('cloneOrgTwinStates');
        }
      });
    },
    async fetchVirtualDeviceDataDetails(virtualDeviceOptions) {
      // in the first version, one would search for the VD-id directly,
      // so both the VD-details AND the states of the VD could be requested in one go
      // now, the external-id is entered and that has to be resolved first, so this runs in 2 phases:
      //
      // I) request the states of the original VD & make a search for devices with the external-id
      // II) if there is a unique match for a VD (phase 1 does noto allow multiple matches)
      //      then proceed by fetching the states on the target/matched VD as well

      let promisesPhase1 = [];
      if (this.vDeviceFetchMapping['vdevice'][virtualDeviceOptions.id]) {
        promisesPhase1.push(Promise.resolve(this.vDeviceFetchMapping['vdevice'][virtualDeviceOptions.id]));
      } else {
        promisesPhase1.push(
        // fetch the newly wired virtual-device. Use the external-id as search param
        // params in order FYI: context, userId, scopeIds, filters, options, orderBy, offset, limit
        ApiService.virtualDeviceFetch(this, null, [], [], null, null, 0, 1, [{
          variable: 'deviceId',
          search: virtualDeviceOptions.id
        }]));
      }
      if (this.vDeviceFetchMapping['vdeviceOrgState'][virtualDeviceOptions.stateId]) {
        promisesPhase1.push(Promise.resolve(this.vDeviceFetchMapping['vdeviceOrgState'][virtualDeviceOptions.stateId]));
      } else {
        promisesPhase1.push(
        // to match the newly wired vdevice with the current one, fetch the ORIGINAL state,
        // then pick the state with the same key from the list just above
        ApiService.digitalTwinGetState(this, virtualDeviceOptions.stateId));
      }
      // -------------
      // FETCH PHASE I
      return Promise.all(promisesPhase1).then(res => {
        let vDeviceMatches = res[0];
        let orgState = res[1] || {};
        this.vDeviceFetchMapping['vdevice'][virtualDeviceOptions.id] = vDeviceMatches;
        this.vDeviceFetchMapping['vdeviceOrgState'][virtualDeviceOptions.stateId] = orgState;
        if (vDeviceMatches.totalCount > 1) {
          // more than 1 match was found, cannot use this v-device
          return {
            id: vDeviceLookupMultipleMatches,
            title: null
          };
        } else if (vDeviceMatches.totalCount === 0) {
          throw new Error('NIOTA_DEVICE_NOT_FOUND');
        }
        const matchedVDeviceId = vDeviceMatches.virtualDevices[0].id;
        let promisesPhase2 = [Promise.resolve(vDeviceMatches), Promise.resolve(orgState)];
        if (this.vDeviceFetchMapping['vdeviceStates'][matchedVDeviceId]) {
          promisesPhase2.push(Promise.resolve(this.vDeviceFetchMapping['vdeviceStates'][matchedVDeviceId]));
        } else {
          promisesPhase2.push(
          // fetch the states on the newly wired virtual-device
          ApiService.digitalTwinFetchStates(this, matchedVDeviceId));
        }
        // -------------
        // FETCH PHASE II
        return Promise.all(promisesPhase2);
      }).then(res => {
        if (res.constructor === Object && res.id === vDeviceLookupMultipleMatches) {
          return res;
        }
        let vDeviceMatch = res[0];
        let orgState = res[1] || {};
        let newStates = res[2];
        const matchedVDevice = vDeviceMatch.virtualDevices[0];
        this.vDeviceFetchMapping['vdeviceStates'][matchedVDevice.id] = newStates;
        if (!newStates) {
          throw new Error('NIOTA_DEVICE_NOT_FOUND');
        }
        return {
          id: matchedVDevice.id,
          title: matchedVDevice.title,
          stateId: (newStates.find(s => s.key === orgState.key) || {}).id,
          key: (newStates.find(s => s.key === orgState.key) || {}).key
        };
      }).catch(error => {
        console.error(error);
        let errorMessage = get(error, 'response.data.message', error.message);
        let errorOutput = this.$i18n_te(`Error.${errorMessage}`) ? this.$i18n_t(`Error.${errorMessage}`) : this.$i18n_t(errorMessage);
        this.$snack(errorOutput, 'error');
        return {
          id: null,
          title: `${virtualDeviceOptions.id} ${this.$i18n_t('NiotaClassic.Devices.snacks.notFound')}`,
          key: null
        };
      });
    },
    handleUpdate(value, idx) {
      this.twinCopiesData[idx]['dynamical-data-routing-destination'] = value;
    }
  }
};